import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import Fuse, { FuseResult, IFuseOptions } from 'fuse.js';
import { autoUpdate, flip, FloatingPortal, offset, shift, size, useDismiss, useFloating, useInteractions } from '@floating-ui/react';
import clsx from 'clsx';
import moment from 'moment';
import { Search as SearchIcon } from '@mui/icons-material';
import Constants from 'relevant-shared/reportData/constants';
import { SearchBar } from './SearchBar';
import { Link } from '../../components/Link/Link';
import { stores } from '../../stores';
import useReports from '../../hooks/useBreakpoint/useReports';
import { usePublishersQuery } from './query';

type SearchIndexKind = 'publisher' | 'site' | 'placement' | 'report';

type SearchIndexType = {
	kind: SearchIndexKind;
	// The full text to fuzzy search on, put in the index
	searchText: string;
	// A shorter text to display in the search results (must be a substring of searchText for highlighting to work)
	text: string;
	// Optional subtext to display in the search results
	subtext?: string;
	// Optional date to display, right-aligned, next to the result title
	date?: string;
	link: string;
	key: string;
}

type SearchResult = Omit<FuseResult<SearchIndexType>, 'matches'> & {
	matches: { start: number; end: number }[] | null;
};

const kinds: Record<SearchIndexKind, { title: string; more?: string }> = {
	'publisher': {
		title: 'Publishers',
		more: '/accounts?f=',
	},
	'site': {
		title: 'Sites',
	},
	'placement': {
		title: 'Placements',
	},
	'report': {
		title: 'Reports',
		more: '/reports?f=',
	},
};

const kindOrder: SearchIndexKind[] = ['publisher', 'site', 'placement', 'report'];

const SEARCH_THROTTLE_MS = 150;

const fuseOptions: IFuseOptions<SearchIndexType> = {
	keys: ['searchText'],
	includeScore: true,
	includeMatches: true,
	ignoreLocation: true,
	useExtendedSearch: true,
	minMatchCharLength: 1,
};

const searchIcon = <SearchIcon className="text-fg-primary size-[18px]!" />;

const floatingMiddleware = [
	offset(7),
	flip({ padding: 8 }),
	shift({ padding: 8 }),
	size({
		padding: 8,
		apply({ rects, availableHeight, elements }) {
			Object.assign(elements.floating.style, {
				width: `${rects.reference.width}px`,
				maxHeight: `${Math.max(160, availableHeight)}px`,
			});
		},
	}),
];

// Merge overlapping start-end pairs in an array of indices
// Ex. [{ start: 0, end: 3 }, { start: 2, end: 5 }] => [{ start: 0, end: 5 }]
function mergeIndices(indices: { start: number; end: number }[]) {
	const sorted = indices.sort((a, b) => a.start - b.start);
	const merged: { start: number; end: number }[] = [];
	let current = sorted[0];
	for (let i = 1; i < sorted.length; i++) {
		if (sorted[i].start <= current.end) {
			current.end = Math.max(current.end, sorted[i].end);
		} else {
			merged.push(current);
			current = sorted[i];
		}
	}
	merged.push(current);
	return merged;
};

type GlobalSearchBarProps = {
	searchBarProps?: React.ComponentProps<typeof SearchBar>;
	placeholder?: string;
	className?: string;
}

export function GlobalSearchBar({ searchBarProps, placeholder, className } : GlobalSearchBarProps) {
	const searchValueRef = useRef('');
	const [searchQuery, setSearchQuery] = useState('');
	const throttleTimerRef = useRef<ReturnType<typeof setTimeout>>();
	const [inFocus, setInFocus] = useState(false);
	const root = useRef<HTMLDivElement>(null);
	// Keyboard navigation of the results. The caret stays in the input (virtual focus),
	// while a highlight moves through the results:
	//  - Up/Down step through every result
	//  - Tab / Shift+Tab jump to the first result of the next/previous category
	//  - Enter opens the currently highlighted result (defaults to the first result)
	// The first result is highlighted by default so Enter works without any navigation.
	const [activeIndex, setActiveIndex] = useState(0);
	const listRef = useRef<Array<HTMLAnchorElement | null>>([]);

	const handleSearchChange = useCallback((value: string) => {
		searchValueRef.current = value;
		clearTimeout(throttleTimerRef.current);
		if (value === '') {
			setSearchQuery('');
			return;
		}
		throttleTimerRef.current = setTimeout(() => {
			setSearchQuery(value);
		}, SEARCH_THROTTLE_MS);
	}, []);

	useEffect(() => () => clearTimeout(throttleTimerRef.current), []);

	// Anchor the results popup with FloatingUI so it flips/shifts to stay within the viewport
	// and gets a max-height (with a scrollable body) when there isn't enough room.
	const { refs, floatingStyles, context } = useFloating({
		open: inFocus,
		onOpenChange: setInFocus,
		placement: 'bottom-start',
		middleware: floatingMiddleware,
		whileElementsMounted: autoUpdate,
	});
	const dismiss = useDismiss(context);
	const { getReferenceProps, getFloatingProps } = useInteractions([dismiss]);

	const { data: publishers } = usePublishersQuery();
	const { reports } = useReports();

	const indexData = useMemo<SearchIndexType[] | undefined>(() => {
		if (!publishers || !reports) {
			return undefined;
		}
		// Construct index for publishers -> sites -> placements (+ make SSPs and Adserver IDs searchable)
		const entries: SearchIndexType[] = [];
		for (const p of publishers) {
			if (p.hidden) {
				continue;
			}
			entries.push({
				kind: 'publisher',
				searchText: p.name,
				text: p.name,
				link: `/accounts/${p.id}`,
				key: p.id,
			});
			for (const s of p.websites as any[]) {
				entries.push({
					kind: 'site',
					searchText: `${p.name} ${s.domain}`,
					text: s.domain,
					subtext: p.name,
					link: `/accounts/${p.id}/sites/${s.id}`,
					key: s.id,
				});
				for (const pl of s.placements as any[]) {
					entries.push({
						kind: 'placement',
						searchText: `${p.name} ${s.domain} ${pl.name}`,
						text: pl.name,
						subtext: `${p.name} » ${s.domain}`,
						link: `/accounts/${p.id}/sites/${s.id}/placements/${pl.id}`,
						key: pl.id,
					});
					for (const ssp of pl.ssps as any[]) {
						entries.push({
							kind: 'placement',
							searchText: `${p.name} ${s.domain} ${pl.name} (SSP: ${ssp.id})`,
							text: `${pl.name} (SSP: ${ssp.id})`,
							subtext: `${p.name} » ${s.domain}`,
							link: `/accounts/${p.id}/sites/${s.id}/placements/${pl.id}`,
							key: pl.id,
						});
					}
					for (const ad of pl.adservers as any[]) {
						// Some adservers are set up to use the SSP ID, so skip if adserver ID is missing or empty string
						const adserverId = ad.settings.placementId ?? ad.settings.location ?? ad.settings.lineItemId;
						if (adserverId) {
							entries.push({
								kind: 'placement',
								searchText: `${p.name} ${s.domain} ${pl.name} (Adserver ID: ${adserverId})`,
								text: `${pl.name} (Adserver ID: ${adserverId})`,
								subtext: `${p.name} » ${s.domain}`,
								link: `/accounts/${p.id}/sites/${s.id}/placements/${pl.id}`,
								key: pl.id,
							});
						}
					}
				}
			}
		}

		// Construct index for reports the user has access to.
		// `reports` is grouped by report type, so flatten it first.
		const userId = stores.identity.userId();
		for (const r of Object.values(reports).flat()) {
			const title = r.settings?.title;
			const location = Constants[r.settings?.type]?.reportLocation;
			if (!title || !location) {
				continue;
			}
			const isMine = !r.isPublic || r.creatorUserInfo?.id === userId;
			entries.push({
				kind: 'report',
				searchText: title,
				text: title,
				subtext: isMine ? 'Created by you' : 'Shared with you',
				date: r.createdDate ? moment(r.createdDate).format('DD/MM/YYYY') : undefined,
				link: `${location}/${r.id}`,
				key: r.id,
			});
		}

		return entries;
	}, [publishers, reports]);

	const fuse = useMemo(() => {
		if (!indexData) {
			return null;
		}
		return new Fuse(indexData, fuseOptions);
	}, [indexData]);

	const searchResults = useMemo(() => {
		if (searchQuery === '' || !fuse) {
			return undefined;
		}

		let value = searchQuery;
		// Always match number sequences exactly, even if they're 1 character long.
		// The single quote character is special fuse.js search syntax that matches the following word exactly.
		// The space at the end is required so that matching for "160x600" and similar still works.
		value = value.replace(/\d+/g, (match) => `'${match} `);

		let results = fuse.search(value, { limit: 15 });
		// Remove duplicate results (since SSP and Adserver ID's are duplicated lines in the index,
		// we only want to show one instance of the placement in the results).
		results = results.filter((r, i) => results.findIndex((r2) => r2.item.key === r.item.key) === i);

		// Fix match indices so we can display the highlight properly
		const resultsFixed: SearchResult[] = results.map((r) => {
			let indices = r.matches?.[0]?.indices.map((m) => ({ start: m[0], end: m[1] }));
			if (!indices) {
				return {
					...r,
					matches: null,
				};
			}
			// Merge overlapping start-end pairs in matches
			indices = mergeIndices(indices);
			// Clamp match indices to only the substring of text inside searchText,
			// cause we only care about rendering matches for the display text, not
			// the whole searchText
			const start = r.item.searchText.indexOf(r.item.text);
			if (start === -1) {
				return {
					...r,
					matches: null,
				};
			}
			const end = r.item.searchText.indexOf(r.item.text) + r.item.text.length;
			indices = indices.filter((m) => m.start >= start && m.end <= end); // Remove any matches that are outside the bounds of the text
			indices = indices.map((m) => ({ start: Math.max(m.start - start, 0), end: Math.min(m.end - start, r.item.text.length - 1) })); // Adjust start and end to be relative to the text
			return {
				...r,
				matches: indices.length > 0 ? indices : null,
			};
		});

		// Group on kind
		const grouped = resultsFixed.reduce((acc, cur) => {
			(acc[cur.item.kind] ??= []).push(cur);
			return acc;
		}, {} as Record<SearchIndexType['kind'], SearchResult[]>);
		// Sort each group by score
		for (const kind of Object.keys(grouped)) {
			grouped[kind] = grouped[kind].sort((a, b) => a.score - b.score);
		}
		return grouped;
	}, [searchQuery, fuse]);

	function handleFocus(e: React.FocusEvent) {
		if (root.current?.contains(e.target as Node)) {
			setInFocus(true);
		}
	}
	function handleBlur(e: React.FocusEvent) {
		const next = e.relatedTarget as Node | null;
		// The popup is rendered in a portal, so also keep focus when moving into it
		if (!root.current?.contains(next) && !refs.floating.current?.contains(next)) {
			setInFocus(false);
		}
	}

	// Sections in display order, a flattened list matching the rendered order (so
	// `activeIndex` maps directly onto `listRef` entries), and the flat index of the
	// first result in each section (the Tab stops).
	const { sections, flatResults, categoryStarts } = useMemo(() => {
		const secs = kindOrder.filter((kind) => (searchResults?.[kind]?.length ?? 0) > 0);
		const flat: SearchResult[] = [];
		const starts: number[] = [];
		for (const kind of secs) {
			starts.push(flat.length);
			flat.push(...searchResults![kind]);
		}
		listRef.current = flat.map(() => null);
		return { sections: secs, flatResults: flat, categoryStarts: starts };
	}, [searchResults]);

	// Highlight the first result by default whenever the results change
	useEffect(() => {
		setActiveIndex(0);
	}, [flatResults]);

	// Keep the highlighted result scrolled into view (the caret stays in the input)
	useEffect(() => {
		if (inFocus) {
			listRef.current[activeIndex]?.scrollIntoView({ block: 'nearest' });
		}
	}, [activeIndex, inFocus]);

	const handleInputKeyDown = useCallback((e: React.KeyboardEvent) => {
		if (flatResults.length === 0) {
			return;
		}
		const last = flatResults.length - 1;
		// Which category currently contains `activeIndex` (categoryStarts is ascending)
		let currentCategory = 0;
		for (let c = 0; c < categoryStarts.length; c++) {
			if (categoryStarts[c] <= activeIndex) {
				currentCategory = c;
			}
		}
		switch (e.key) {
			case 'ArrowDown':
				e.preventDefault();
				setActiveIndex((i) => Math.min(i + 1, last));
				break;
			case 'ArrowUp':
				e.preventDefault();
				setActiveIndex((i) => Math.max(i - 1, 0));
				break;
			case 'Enter':
				e.preventDefault();
				listRef.current[activeIndex]?.click();
				break;
			case 'Tab': {
				// Step the highlight to the first result of the adjacent category.
				// At the boundaries fall through to the browser default so focus can
				// still leave the search field entirely.
				const nextCategory = e.shiftKey ? currentCategory - 1 : currentCategory + 1;
				if (nextCategory >= 0 && nextCategory < categoryStarts.length) {
					e.preventDefault();
					setActiveIndex(categoryStarts[nextCategory]);
				}
				break;
			}
			default:
				break;
		}
	}, [activeIndex, flatResults.length, categoryStarts]);

	return (
		<div
			ref={(el) => {
				root.current = el;
				refs.setReference(el);
			}}
			className={clsx('relative w-full max-w-[600px]', className)}
			{...getReferenceProps({
				onFocus: handleFocus,
				onBlur: handleBlur,
				onKeyDown: handleInputKeyDown,
			})}
		>
			<SearchBar
				onChange={handleSearchChange}
				placeholder={placeholder}
				icon={searchIcon}
				{...searchBarProps}
				className={clsx(
					'[&_input]:text-xs [&_input]:tracking-[0.12px] [&_.closeIcon]:size-4!',
					'hover:shadow-[0px_4px_7px_2px_rgba(25,25,25,0.05)] focus-within:shadow-[0px_4px_7px_2px_rgba(25,25,25,0.05)]',
					searchBarProps?.className,
				)}
			/>
			{(searchResults && inFocus) && (
				<FloatingPortal>
					<div
						ref={refs.setFloating}
						style={floatingStyles}
						{...getFloatingProps()}
						className="scrollbar-thin z-10 flex min-w-[300px] flex-col gap-2 overflow-y-auto rounded-md border border-borders-primary bg-bg-static-surface_default p-2 shadow-[0px_4px_7px_2px_rgba(25,25,25,0.05)]"
					>
						{sections.map((kind, sectionIdx) => {
							// Offset of this section's first item within the flat results list
							const sectionOffset = sections
								.slice(0, sectionIdx)
								.reduce((n, k) => n + searchResults[k].length, 0);
							return (
							<div key={kind} className="flex flex-col">
								<div className="flex items-center gap-4 pb-1 pl-3 pr-2 pt-2">
									<h3 className="text-xs font-semibold uppercase tracking-[0.11em] text-text-quaternary whitespace-nowrap">{kinds[kind].title}</h3>
									<div className="h-px flex-1 bg-borders-primary" />
								</div>
								{searchResults[kind].map((r, i) => {
									const index = sectionOffset + i;
									return (
									<a
										key={r.item.key}
										ref={(node) => { listRef.current[index] = node; }}
										id={`global-search-result-${index}`}
										href={r.item.link}
										tabIndex={-1}
										onMouseEnter={() => setActiveIndex(index)}
										className={clsx(
											'flex cursor-pointer flex-col gap-1 rounded-[4px] py-2 pl-3 pr-2 transition hover:bg-bg-interactive-light-bg-active_hover focus:bg-bg-interactive-light-bg-active_pressing',
											activeIndex === index && 'bg-bg-interactive-light-bg-active_hover',
										)}
									>
										<div className="flex items-center justify-between gap-2">
											<p className="min-w-0 flex-1 truncate text-sm tracking-[0.14px] text-text-primary">
												{r.matches ? (
													<>
														{r.matches.map((m, j) => (
															<React.Fragment key={j}>
																{r.item.text.slice(j === 0 ? 0 : r.matches[j-1].end+1, m.start)}
																<span className="font-semibold">
																	{r.item.text.slice(m.start, m.end+1)}
																</span>
															</React.Fragment>
														))}
														{r.item.text.slice(r.matches[r.matches.length-1].end+1)}
													</>
												) : (
													r.item.text
												)}
											</p>
											{r.item.date && (
												<span className="shrink-0 text-xs tracking-[0.12px] text-text-secondary">{r.item.date}</span>
											)}
										</div>
										{r.item.subtext && (
											<span className="block truncate text-xs tracking-[0.12px] text-text-tertiary">{r.item.subtext}</span>
										)}
									</a>
									);
								})}
								{kinds[kind].more && (
									<Link to={`${kinds[kind].more}${searchQuery || searchValueRef.current}`} className="block py-2 pl-3 pr-2 text-sm">Show all results</Link>
								)}
							</div>
							);
						})}
						{sections.length === 0 && (
							<div className="py-6 text-center text-sm text-text-primary">No results found</div>
						)}
					</div>
				</FloatingPortal>
			)}
		</div>
	);
}
